export type LineExportMessageType =
  | 'text'
  | 'photo'
  | 'sticker'
  | 'video'
  | 'file'
  | 'audio'
  | 'location'
  | 'call_start'
  | 'call_end'
  | 'deleted'
  | 'system'

export interface LineExportMessage {
  date: string
  time: string
  sender: string
  body: string
  message_type: LineExportMessageType
}

export interface LineExportParseWarning {
  line: number
  code: 'message_without_date' | 'orphan_continuation' | 'malformed_message'
  message: string
  text: string
}

export interface LineExportParseResult {
  messages: LineExportMessage[]
  warnings: LineExportParseWarning[]
}

const dateLinePattern = /^(\d{4})\/(\d{2})\/(\d{2})\([^)]*\)$/
const messageLinePattern = /^(\d{2}:\d{2})\t([^\t]*)\t([\s\S]*)$/
const timestampPrefixPattern = /^\d{2}:\d{2}\t/

export function parseLineExport(text: string): LineExportParseResult {
  const messages: LineExportMessage[] = []
  const warnings: LineExportParseWarning[] = []
  let currentDate: string | null = null
  let currentMessage: LineExportMessage | null = null

  for (const [index, line] of text.split(/\r?\n/).entries()) {
    if (!line) continue

    const lineNumber = index + 1
    const dateMatch = dateLinePattern.exec(line)
    if (dateMatch) {
      currentDate = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`
      currentMessage = null
      continue
    }

    const messageMatch = messageLinePattern.exec(line)
    if (messageMatch) {
      if (!currentDate) {
        warnings.push(buildWarning(
          lineNumber,
          'message_without_date',
          'Message line appeared before any date separator.',
          line
        ))
        currentMessage = null
        continue
      }

      currentMessage = {
        date: currentDate,
        time: messageMatch[1],
        sender: messageMatch[2],
        body: messageMatch[3],
        message_type: classifyMessageType(messageMatch[3])
      }
      messages.push(currentMessage)
      continue
    }

    if (timestampPrefixPattern.test(line)) {
      warnings.push(buildWarning(
        lineNumber,
        'malformed_message',
        'Timestamped line did not match the expected LINE export message format.',
        line
      ))
      currentMessage = null
      continue
    }

    if (currentMessage) {
      currentMessage.body = `${currentMessage.body}\n${line}`
      continue
    }

    warnings.push(buildWarning(
      lineNumber,
      'orphan_continuation',
      'Continuation line appeared before any message.',
      line
    ))
  }

  return { messages, warnings }
}

function buildWarning(
  line: number,
  code: LineExportParseWarning['code'],
  message: string,
  text: string
): LineExportParseWarning {
  return { line, code, message, text }
}

function classifyMessageType(body: string): LineExportMessageType {
  if (body === '[写真]') return 'photo'
  if (body === '[スタンプ]') return 'sticker'
  if (body === '[動画]') return 'video'
  if (body === '[ファイル]') return 'file'
  if (body === '[音声メッセージ]') return 'audio'
  if (body === '[位置情報]') return 'location'
  if (body.includes('グループ通話が開始されました')) return 'call_start'
  if (body.includes('グループ通話が終了しました')) return 'call_end'
  if (body.includes('メッセージの送信を取り消しました')) return 'deleted'
  if (body.includes('グループに追加しました') || body.includes('グループを退会しました')) {
    return 'system'
  }
  return 'text'
}
